// =============================================================================
// clients-client.ts — Client API référentiel CLIENTS bénéficiaires ADMIN (S6), DOUBLABLE.
// =============================================================================
// Miroir des endpoints ADMIN du BC client-reference (apps/api/src/client-reference/
// client-reference-admin.controller.ts). Comme les autres clients UI (suppliers,
// legal, parameters), expose une INTERFACE (ClientsClient) consommée par les hooks
// TanStack ; l'impl HTTP réelle (fetch) viendra derrière la MÊME interface sans
// toucher l'UI (stub-first).
//
// `client_reference` est GLOBAL admin-only (DDD §3.3.1) : grain SIREN (AR, §3.3.3).
// L'AUTORITÉ (RBAC AdminSessionGuard, émission DE.Client*, validité des transitions
// Actif↔Archive, no-DELETE §3.3.5 inv.3) est l'API — l'UI relaie l'intention.
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db) : l'enum d'état est
// répliqué en union de littéraux ASCII (parité Prisma ClientState). La SPA ne couple
// PAS au paquet DB.
//
// PII / MARQUEURS DIFFÉRÉS (NE PAS inventer de données — DDD §3.3) :
//   - ContactReferent (nom/email/téléphone) : PII informationnelle (§3.3.5 inv.2),
//     exclue de Pulse (RULE_REF_036) ET du read-model liste — non exposée ici.
//   - participations campagne : BC Campaign ABSENT avant S10 → différé-visible.

import { HttpApiError } from '@/api/http-error';

/** État d'un Client (parité Prisma ClientState ; ASCII — accents = i18n front). */
export type ClientStateDto = 'Actif' | 'Archive';

/** Ligne du référentiel clients (§2.3). Champs OWNED client-reference. */
export interface ClientListItemDto {
  readonly siren: string;
  readonly raisonSociale: string;
  readonly secteurActivite: string;
  readonly state: ClientStateDto;
  /**
   * Référence interne JEKO (nullable) — recherchable côté liste (DBU). NON-PII (identifiant de
   * gestion). Miroir du read-model API. Les attributs segment/taille et le ContactReferent (PII)
   * NE sont PAS exposés à la liste (§3.3.5 inv.2) — ils vivent sur la fiche détail (lot séparé).
   */
  readonly referenceInterneJeko: string | null;
  /** Horodatage de dernière modification (ISO-8601, non-PII). Miroir du read-model API. */
  readonly lastModifiedAt: string;
  /**
   * Nombre de campagnes liées HISTORIQUEMENT à ce client (R15-CMP-01, colonne « campagnes liées »).
   * Comptage cross-BC campaign résolu et agrégé côté API (BATCH), non-nominatif. `0` si aucune.
   */
  readonly campaignCount: number;
  /**
   * Libellé de l'AUTEUR de la dernière modification (read-model API). Vaut « — » tant que
   * l'annuaire admin n'existe pas (résolution nom d'admin DIFFÉRÉE — chantier séparé). Le front
   * NE résout RIEN : il affiche ce libellé tel quel. AUCUN UUID auteur n'est exposé au front
   * (minimisation SECOPS R-5.1) — seulement ce label déjà résolu (ou dégradé) par l'API.
   */
  readonly lastModifiedByLabel: string;
}

/**
 * Contact référent facultatif (PII WRITE-ONLY, §3.3.5 inv.2). Saisi à la création, persisté côté
 * API ; JAMAIS relu par la liste (il vivra sur la fiche détail, lot séparé). Tous champs facultatifs.
 */
export interface ContactReferentInputDto {
  readonly nom?: string;
  readonly fonction?: string;
  readonly email?: string;
  readonly telephone?: string;
}

/** Contact référent (fiche détail — PII informationnelle ; miroir HTTP, JAMAIS dans la liste). */
export interface ContactReferentDto {
  readonly referentId: string;
  readonly nom: string | null;
  readonly fonction: string | null;
  readonly email: string | null;
  readonly telephone: string | null;
}

/** Fiche DÉTAILLÉE d'un Client (§2.3 : dossier + attributs métier + contact référent). Miroir read-model API. */
export interface ClientDetailDto {
  readonly siren: string;
  readonly raisonSociale: string;
  readonly secteurActivite: string;
  readonly segment: string | null;
  readonly taille: string | null;
  readonly referenceInterneJeko: string | null;
  readonly state: ClientStateDto;
  readonly lastModifiedAt: string;
  readonly lastModifiedByLabel: string;
  readonly contactsReferents: readonly ContactReferentDto[];
}

/** Une campagne dans la chronologie d'un client (§2.3, DBU l.826). Miroir HTTP, aucune PII. */
export interface ClientCampaignHistoryItemDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly state: string;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly supplierCount: number;
  readonly createdAt: string;
}

/** Corps de création d'un Client (le SIREN est la donnée saisie ; grain Client §3.3.3). */
export interface CreateClientInput {
  readonly siren: string;
  readonly raisonSociale: string;
  readonly secteurActivite: string;
  /** Attribut métier facultatif — segment. */
  readonly segment?: string;
  /** Attribut métier facultatif — taille. */
  readonly taille?: string;
  /** Référence interne JEKO facultative — recherchable côté liste (DBU). */
  readonly referenceInterneJeko?: string;
  /** Contact référent facultatif (PII WRITE-ONLY) — envoyé à l'API, jamais relu par la liste. */
  readonly contactReferent?: ContactReferentInputDto;
}

/** Résultat de la création (SIREN du client créé). */
export interface CreateClientResult {
  readonly siren: string;
}

/**
 * Corps d'édition d'un Client (S23-CLIENT-EDIT, DBU §2.3 l.828). Le SIREN cible la ressource (URL).
 * Un attribut ABSENT reste inchangé ; un attribut nullable à `null` est effacé. `contactReferent`
 * présent (objet ou `null`) ⇒ remplacement du bloc contact référent (`null` = effacement) ; absent ⇒
 * contact inchangé. Aucune règle métier ici : l'API décide (émission DE.ClientEdited, audit).
 */
export interface EditClientInput {
  readonly siren: string;
  readonly raisonSociale?: string;
  readonly secteurActivite?: string;
  readonly segment?: string | null;
  readonly taille?: string | null;
  readonly referenceInterneJeko?: string | null;
  /** Bloc contact référent (PII WRITE-ONLY) — envoyé à l'API, jamais relu par la liste. */
  readonly contactReferent?: ContactReferentInputDto | null;
}

/**
 * Erreur applicative renvoyée par l'API admin (HTTP 4xx). L'UI l'affiche telle
 * quelle ; elle ne réinterprète jamais la règle métier (autorité = API).
 */
export class ClientsApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'ClientsApiError');
  }
}

/**
 * Contrat du client référentiel CLIENTS ADMIN — miroir des endpoints S6. Aucune
 * règle métier ici : les transitions/validations (Actif↔Archive, unicité SIREN,
 * émission DE.Client*) sont l'autorité de l'API (RBAC AdminSessionGuard). L'UI
 * relaie l'intention.
 */
export interface ClientsClient {
  /** GET /admin/clients — référentiel clients (§2.3, GLOBAL admin-only). */
  listClients(): Promise<readonly ClientListItemDto[]>;

  /** GET /admin/clients/:siren — fiche détail (dossier + attributs + contact référent). 404 si absent. */
  getClientDetail(siren: string): Promise<ClientDetailDto>;

  /** GET /admin/clients/:siren/campaigns — chronologie des campagnes liées (JAMAIS 404 ; [] si aucune). */
  getClientCampaigns(siren: string): Promise<readonly ClientCampaignHistoryItemDto[]>;

  /** POST /admin/clients — crée une fiche Client (Actif + DE.ClientCreated). */
  createClient(input: CreateClientInput): Promise<CreateClientResult>;

  /** PATCH /admin/clients/:siren — édite une fiche Client (attributs métier + contact) + DE.ClientEdited. */
  editClient(input: EditClientInput): Promise<void>;

  /** POST /admin/clients/:siren/archive — archive (Actif→Archive, no-DELETE) — DESTRUCTIF (confirmation UI). */
  archiveClient(siren: string): Promise<void>;

  /** POST /admin/clients/:siren/reactivate — réactive (Archive→Actif). */
  reactivateClient(siren: string): Promise<void>;
}
